<template>
  <aside class="hidden w-80 shrink-0 flex-col border-l bg-muted/10 xl:flex">
    <div class="flex h-16 items-center border-b px-4">
      <div class="min-w-0">
        <h2 class="truncate text-sm font-semibold">
          {{ title }}
        </h2>
        <p class="text-xs capitalize text-muted-foreground">
          {{ conversation.type }}
        </p>
      </div>
    </div>

    <ScrollArea class="min-h-0 flex-1">
      <div class="flex flex-col gap-5 p-4">
        <Alert v-if="conversationActionError" variant="destructive">
          <AlertDescription>{{ conversationActionError }}</AlertDescription>
        </Alert>

        <section class="flex flex-col items-center gap-3 text-center">
          <Avatar size="base">
            <AvatarImage v-if="conversation.avatar_path" :src="conversation.avatar_path" />
            <AvatarFallback>{{ initials }}</AvatarFallback>
          </Avatar>

          <div class="min-w-0">
            <div class="truncate text-base font-semibold">
              {{ title }}
            </div>
            <div class="text-xs text-muted-foreground">
              {{ activeParticipants.length }} participants
            </div>
          </div>
        </section>

        <Separator />

        <section v-if="isGroup" class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-sm font-medium">
              Group
            </h3>
            <Badge variant="secondary">
              {{ currentParticipantRole }}
            </Badge>
          </div>

          <div class="flex flex-col gap-2 text-xs text-muted-foreground">
            <p class="truncate">
              {{ conversation.title || 'Untitled group' }}
            </p>
            <Button variant="outline" size="sm" class="gap-2" :disabled="!canEditGroupDetails" :loading="conversationStore.isUpdatingConversation" @click="openGroupDetailsModal">
              <Pencil class="h-4 w-4" />
              Edit group
            </Button>
          </div>
        </section>

        <section v-else class="flex flex-col gap-2">
          <h3 class="text-sm font-medium">
            Direct
          </h3>
          <p class="text-xs text-muted-foreground">
            {{ otherDirectParticipant?.user?.name || 'Conversation details' }}
          </p>
        </section>

        <Separator />

        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-sm font-medium">
              Participants
            </h3>
            <Badge variant="secondary">
              {{ activeParticipants.length }}
            </Badge>
          </div>

          <div v-if="isGroup">
            <UserSelect :key="participantUserSelectKey" ref="participantUserSelect" v-model="selectedParticipantUser" placeholder="Search users to add" :disabled="!canManageParticipants" :is-error="Boolean(participantValidationMessage)" @selected="addParticipant" />
          </div>
          <p v-if="participantValidationMessage" class="text-xs text-destructive">
            {{ participantValidationMessage }}
          </p>

          <div class="flex flex-col gap-2">
            <div v-for="participant in activeParticipants" :key="participant.id" class="flex items-center gap-3 rounded-md border bg-background px-3 py-2">
              <Avatar size="sm">
                <AvatarImage v-if="participant.user?.profile_photo_url" :src="participant.user.profile_photo_url" />
                <AvatarFallback>{{ userInitials(participant.user?.name) }}</AvatarFallback>
              </Avatar>

              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium">
                  {{ participant.user?.name || `User #${participant.user_id}` }}
                </div>
                <div class="text-xs capitalize text-muted-foreground">
                  {{ participant.role }}
                </div>
              </div>

              <Badge :variant="isParticipantOnline(participant) ? 'default' : 'secondary'" class="shrink-0">
                {{ isParticipantOnline(participant) ? 'Online' : 'Offline' }}
              </Badge>

              <div v-if="isGroup && participant.user_id !== currentUserId" class="flex shrink-0 items-center gap-1">
                <Button v-if="canManageRoles && participant.role === 'member'" variant="ghost" size="icon-sm" :disabled="participantStore.isManagingParticipants" @click="updateParticipantRole(participant, 'admin')">
                  <ShieldPlus class="h-4 w-4" />
                </Button>
                <Button v-if="canManageRoles && participant.role === 'admin'" variant="ghost" size="icon-sm" :disabled="participantStore.isManagingParticipants" @click="updateParticipantRole(participant, 'member')">
                  <ShieldMinus class="h-4 w-4" />
                </Button>
                <Button v-if="canRemoveParticipant(participant)" variant="ghost" size="icon-sm" :disabled="participantStore.isManagingParticipants" @click="confirmRemoveParticipant(participant)">
                  <UserMinus class="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <template v-if="isGroup">
          <Separator />

          <Button variant="destructive" size="sm" :disabled="participantStore.isManagingParticipants" :loading="participantStore.isManagingParticipants" @click="confirmLeaveConversation">
            Leave group
          </Button>
        </template>
      </div>
    </ScrollArea>

    <GroupConversationDetailsModal ref="groupDetailsModal" :conversation="conversation" @conversation-action-started="emit('conversationActionStarted')" @conversation-action-failed="setConversationActionError" @conversation-updated="emit('conversationUpdated', $event)" />
  </aside>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { Pencil, ShieldMinus, ShieldPlus, UserMinus } from 'lucide-vue-next'
import UserSelect from '@User-manager/components/users/UserSelect.vue'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../../../resources/js/components/ui/avatar/index.js'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { ScrollArea } from '../../../../../../../resources/js/components/ui/scroll-area/index.js'
import { Separator } from '../../../../../../../resources/js/components/ui/separator/index.js'
import { useChatStore } from '../../stores/chatStore.js'
import { useConversationParticipantStore } from '../../stores/conversationParticipantStore.js'
import { useConversationStore } from '../../stores/conversationStore.js'
import GroupConversationDetailsModal from './GroupConversationDetailsModal.vue'

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  currentUserId: {
    type: [Number, String, null],
    default: null,
  },
  onlineUserIds: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits([
  'conversationActionStarted',
  'conversationActionFailed',
  'conversationUpdated',
  'participantsAdded',
  'participantRemoved',
  'conversationLeft',
  'participantRoleUpdated',
])

const chatStore = useChatStore()
const conversationStore = useConversationStore()
const participantStore = useConversationParticipantStore()

const groupDetailsModal = ref(null)
const participantUserSelect = ref(null)
const participantUserSelectKey = ref(0)
const selectedParticipantUser = ref(null)
const conversationActionError = ref('')
const participantValidationMessage = ref('')

const activeParticipants = computed(() => {
  return props.conversation.participants?.filter((participant) => participant.left_at === null) || []
})

const activeParticipantUserIds = computed(() => {
  return activeParticipants.value.map((participant) => Number(participant.user_id))
})

const currentParticipant = computed(() => {
  return activeParticipants.value.find((participant) => Number(participant.user_id) === Number(props.currentUserId)) || null
})

const otherDirectParticipant = computed(() => {
  return activeParticipants.value.find((participant) => Number(participant.user_id) !== Number(props.currentUserId)) || null
})

const isGroup = computed(() => props.conversation.type === 'group')
const currentParticipantRole = computed(() => currentParticipant.value?.role || 'member')
const canManageGroup = computed(() => ['owner', 'admin'].includes(currentParticipantRole.value))
const canManageRoles = computed(() => currentParticipantRole.value === 'owner')
const canEditGroupDetails = computed(() => canManageGroup.value && !conversationStore.isUpdatingConversation)
const canManageParticipants = computed(() => canManageGroup.value && !participantStore.isManagingParticipants)

const initials = computed(() => {
  return userInitials(props.title)
})

const userInitials = (name) => {
  return String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'U'
}

const canRemoveParticipant = (participant) => {
  if (!canManageGroup.value || participant.role === 'owner') {
    return false
  }

  if (currentParticipantRole.value === 'owner') {
    return true
  }

  return currentParticipantRole.value === 'admin' && participant.role === 'member'
}

const isParticipantOnline = (participant) => {
  return props.onlineUserIds.map((userId) => Number(userId)).includes(Number(participant.user_id))
}

const openGroupDetailsModal = () => {
  conversationActionError.value = ''
  groupDetailsModal.value?.openModal()
}

const addParticipant = async (user) => {
  conversationActionError.value = ''
  participantValidationMessage.value = ''

  const userId = Number(user?.id)

  if (!Number.isInteger(userId) || userId <= 0) {
    participantValidationMessage.value = 'Select a user to add.'
    resetParticipantUserSelect()
    return
  }

  if (activeParticipantUserIds.value.includes(userId)) {
    participantValidationMessage.value = 'That user is already in this group.'
    resetParticipantUserSelect()
    return
  }

  emit('conversationActionStarted')

  try {
    const participants = await participantStore.addParticipants(props.conversation.id, [userId])
    emit('participantsAdded', participants)
    resetParticipantUserSelect()
  } catch (error) {
    setConversationActionError(errorMessageFrom(error, 'Unable to add participants.'))
    resetParticipantUserSelect()
  }
}

const resetParticipantUserSelect = async ({ focus = true } = {}) => {
  selectedParticipantUser.value = null
  participantUserSelectKey.value += 1

  if (!focus) {
    return
  }

  await nextTick()
  participantUserSelect.value?.focusInput()
}

const confirmRemoveParticipant = async (participant) => {
  if (!window.confirm(`Remove ${participant.user?.name || `User #${participant.user_id}`} from this group?`)) {
    return
  }

  conversationActionError.value = ''
  emit('conversationActionStarted')

  try {
    const removedParticipant = await participantStore.removeParticipant(props.conversation.id, participant.user_id)
    emit('participantRemoved', removedParticipant)
  } catch (error) {
    setConversationActionError(errorMessageFrom(error, 'Unable to remove participant.'))
  }
}

const confirmLeaveConversation = async () => {
  if (!window.confirm('Leave this group?')) {
    return
  }

  conversationActionError.value = ''
  emit('conversationActionStarted')

  try {
    const participant = await participantStore.leaveConversation(props.conversation.id)
    chatStore.unsubscribeFromConversation(props.conversation.id)

    if (String(chatStore.activeConversationId) === String(props.conversation.id)) {
      chatStore.clearActiveConversation()
    }

    emit('conversationLeft', { conversation: props.conversation, participant })
  } catch (error) {
    setConversationActionError(errorMessageFrom(error, 'Unable to leave conversation.'))
  }
}

const updateParticipantRole = async (participant, role) => {
  if (!participant?.user_id || !role) {
    return
  }

  conversationActionError.value = ''
  emit('conversationActionStarted')

  try {
    const updatedParticipant = await participantStore.updateParticipantRole(props.conversation.id, participant.user_id, role)
    emit('participantRoleUpdated', updatedParticipant)
  } catch (error) {
    setConversationActionError(errorMessageFrom(error, 'Unable to update participant role.'))
  }
}

const setConversationActionError = (message) => {
  conversationActionError.value = message
  emit('conversationActionFailed', message)
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors) {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

watch(
  () => props.conversation.id,
  () => {
    resetParticipantUserSelect({ focus: false })
    participantValidationMessage.value = ''
  },
  { immediate: true }
)
</script>
